import React, { useState, useEffect, useCallback } from 'react';
import {
    Users,
    Search,
    Trash2,
    Ban,
    CheckCircle,
    Mail,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Loader2,
    AlertCircle
} from 'lucide-react';
import { AdminSubscribersService } from '../../services/admin/SubscribersServices';
import { useNotification } from '../../context/NotificationContext';
import ConfirmationModal from '../../components/admin/ConfirmationModal';

export default function SubscribersPage() {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [pagination, setPagination] = useState({
        current_page: 1,
        pages: 1,
        total: 0
    });
    const [isActionLoading, setIsActionLoading] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, subscriber: null });
    const { showNotification } = useNotification();

    const fetchSubscribers = useCallback(async (page = 1, search = "") => {
        setLoading(true);
        try {
            const response = await AdminSubscribersService.getSubscribers(page, 20, search);
            if (response.status === "success") {
                setSubscribers(response.subscribers);
                setPagination({
                    current_page: response.current_page,
                    pages: response.pages,
                    total: response.total
                });
            }
        } catch (error) {
            showNotification({ message: "Failed to load subscribers", type: "error" });
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    useEffect(() => {
        fetchSubscribers(1, searchTerm);
    }, [searchTerm, fetchSubscribers]);

    const handleToggleStatus = async (subscriber) => {
        const newStatus = subscriber.status === 'active' ? 'blocked' : 'active';
        setIsActionLoading(subscriber.id);
        try {
            const response = await AdminSubscribersService.updateStatus(subscriber.id, newStatus);
            if (response.status === "success") {
                showNotification({ message: response.message, type: "success" });
                setSubscribers(prev => prev.map(s =>
                    s.id === subscriber.id ? { ...s, status: newStatus } : s
                ));
            }
        } catch (error) {
            showNotification({ message: "Failed to update status", type: "error" });
        } finally {
            setIsActionLoading(null);
        }
    };

    const handleDelete = (subscriber) => {
        setDeleteModal({ isOpen: true, subscriber });
    };

    const confirmDelete = async () => {
        const { subscriber } = deleteModal;
        if (!subscriber) return;

        setIsActionLoading(subscriber.id);
        setDeleteModal({ isOpen: false, subscriber: null });
        try {
            const response = await AdminSubscribersService.deleteSubscriber(subscriber.id);
            if (response.status === "success") {
                showNotification({ message: response.message, type: "success" });
                setSubscribers(prev => prev.filter(s => s.id !== subscriber.id));
                setPagination(prev => ({ ...prev, total: prev.total - 1 }));
            }
        } catch (error) {
            showNotification({ message: "Failed to delete subscriber", type: "failed" });
        } finally {
            setIsActionLoading(null);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <>
            <div className="p-6 lg:p-10 max-w-[1600px] mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-jazz-brown/10 rounded-xl text-jazz-brown">
                                <Users size={24} />
                            </div>
                            <h1 className="text-3xl font-bold text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                                Newsletter Subscribers
                            </h1>
                        </div>
                        <p className="text-black dark:text-white/50 text-sm font-medium">
                            Manage and view all users subscribed to your newsletter
                        </p>
                    </div>

                    <div className="flex items-center gap-4 bg-white dark:bg-[#1A1A1A] px-4 py-2 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <div className="text-right hidden sm:block">
                            <p className="text-xs font-bold text-black dark:text-white/30 uppercase tracking-widest">Total Subscribers</p>
                            <p className="text-xl font-bold text-black dark:text-white leading-none">{pagination.total}</p>
                        </div>
                        <div className="w-px h-8 bg-black/5 dark:white/5 mx-2 hidden sm:block" />
                        <Users className="text-jazz-brown opacity-50" size={20} />
                    </div>
                </div>

                {/* Filters & Search */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-3xl border border-black/5 dark:border-white/5 shadow-sm mb-8">
                    <div className="relative w-full md:w-[400px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20" size={18} />
                        <input
                            type="text"
                            placeholder="Search by email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-[#FAFAFA] dark:bg-[#222] border-none rounded-2xl text-sm focus:ring-2 focus:ring-jazz-brown/20 transition-all outline-none"
                        />
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white dark:bg-[#1A1A1A] rounded-[32px] border border-black/5 dark:border-white/5 shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-black/5 dark:border-white/5 bg-gray-50/50 dark:bg-zinc-900/50">
                                    <th className="px-8 py-6 text-xs font-bold text-black dark:text-white/40 uppercase tracking-[0.2em]">Subscriber</th>
                                    <th className="px-8 py-6 text-xs font-bold text-black dark:text-white/40 uppercase tracking-[0.2em]">Status</th>
                                    <th className="px-8 py-6 text-xs font-bold text-black dark:text-white/40 uppercase tracking-[0.2em]">Join Date</th>
                                    <th className="px-8 py-6 text-xs font-bold text-black dark:text-white/40 uppercase tracking-[0.2em] text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5 dark:divide-white/5">
                                {loading ? (
                                    <tr>
                                        <td colSpan="4" className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-4">
                                                <Loader2 className="animate-spin text-jazz-brown" size={40} />
                                                <p className="text-sm font-medium text-black dark:text-white/50">Loading subscribers...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : subscribers.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-4">
                                                <AlertCircle className="text-black/10 dark:text-white/10" size={60} />
                                                <p className="text-xl font-medium text-black dark:text-white/50 tracking-tight">No subscribers found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    subscribers.map((subscriber) => (
                                        <tr key={subscriber.id} className="group hover:bg-gray-50/50 dark:hover:bg-white/[0.02] transition-colors">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-jazz-brown/10 flex items-center justify-center text-jazz-brown ring-4 ring-jazz-brown/5">
                                                        <Mail size={18} />
                                                    </div>
                                                    <span className="text-sm font-bold text-black dark:text-white tracking-tight">{subscriber.email}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${subscriber.status === 'active'
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                                    : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                                                    }`}>
                                                    {subscriber.status === 'active' ? <CheckCircle size={12} /> : <Ban size={12} />}
                                                    {subscriber.status}
                                                </span>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-2 text-black dark:text-white/40 text-sm">
                                                    <Calendar size={14} />
                                                    {formatDate(subscriber.created_at)}
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleToggleStatus(subscriber)}
                                                        disabled={isActionLoading === subscriber.id}
                                                        title={subscriber.status === 'active' ? 'Block' : 'Unblock'}
                                                        className={`p-2.5 rounded-xl transition-all ${subscriber.status === 'active'
                                                            ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/10 dark:text-red-400'
                                                            : 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/10 dark:text-green-400'
                                                            }`}
                                                    >
                                                        {isActionLoading === subscriber.id ? <Loader2 className="animate-spin" size={18} /> : (subscriber.status === 'active' ? <Ban size={18} /> : <CheckCircle size={18} />)}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(subscriber)}
                                                        disabled={isActionLoading === subscriber.id}
                                                        className="p-2.5 bg-gray-100 text-black hover:bg-red-500 hover:text-white dark:bg-zinc-800 dark:text-white/60 rounded-xl transition-all"
                                                        title="Delete"
                                                    >
                                                        {isActionLoading === subscriber.id ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <div className="px-8 py-6 border-t border-black/5 dark:border-white/5 flex items-center justify-between bg-gray-50/30 dark:bg-transparent">
                            <p className="text-xs font-semibold text-black dark:text-white/40 uppercase tracking-widest">
                                Page {pagination.current_page} of {pagination.pages}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => fetchSubscribers(pagination.current_page - 1, searchTerm)}
                                    disabled={pagination.current_page === 1 || loading}
                                    className="p-2 rounded-lg border border-black/5 dark:border-white/5 hover:bg-white dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <button
                                    onClick={() => fetchSubscribers(pagination.current_page + 1, searchTerm)}
                                    disabled={pagination.current_page === pagination.pages || loading}
                                    className="p-2 rounded-lg border border-black/5 dark:border-white/5 hover:bg-white dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, subscriber: null })}
                onConfirm={confirmDelete}
                title="Delete Subscriber"
                message={`Are you sure you want to delete ${deleteModal.subscriber?.email}? This action cannot be undone.`}
                confirmText="Delete"
                isDanger={true}
            />
        </>
    );
}
